/**
 * Creator → Pro Studio hand-off. Reads `?song=<library id>` and
 * `?intent=<record|upload|stems|recordings|session|beat|fx>` on /studio,
 * loads the song into the engine (once), syncs the transport React state
 * and runs the intent action. Also re-syncs loadedCount/maxDur from the
 * shared engine on mount so returning from the Creator never leaves PLAY gated.
 */
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getSong, loadUrlIntoChannel, playableUrl } from "@/lib/creatorApi";
import { ensureVocalTakeLoaded } from "@/lib/creatorVocal";
import { padCh } from "@/components/daw/dawHelpers";

const INTENT_HINTS = {
  record: { title: "Ready to record your voice", description: "Pick a free channel, press its REC button and sing along — the song plays underneath." },
  upload: { title: "Import a song or beat", description: "Choose a file to drop it onto the first free channel." },
  stems: { title: "Separate song parts", description: "Load a song, then use Extract Stems on its channel to split vocals, drums, bass and more." },
  beat: { title: "Build a beat", description: "The drum machine is open — tap a kit and program your pattern." },
  fx: { title: "Vocal effects", description: "Your voice channel is selected — press its FX button to shape the sound (EQ, compressor, reverb…)." },
};

export default function useStudioIntent({
  engine, loadedCount, setLoadedCount, setMaxDur, setSelectedChannel, setVisibleChannels, actions = {},
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const firedRef = useRef(new Set());
  const engineRef = useRef(engine);
  engineRef.current = engine;
  const actionsRef = useRef(actions);
  actionsRef.current = actions;

  // Cross-route return: the singleton engine may already hold audio.
  useEffect(() => {
    const eng = engineRef.current;
    if (!eng?.channels) return;
    const n = eng.channels.filter((c) => c?.buffer).length;
    if (n > 0 && loadedCount === 0) {
      setLoadedCount(n);
      setMaxDur(eng.maxDuration || 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const songId = params.get("song");
    const intent = params.get("intent");
    const chParam = params.get("ch");
    if (!songId && !intent) return;
    const key = `${songId || ""}|${intent || ""}|${chParam || ""}`;
    if (firedRef.current.has(key)) return;
    if (!engineRef.current?.channels) return;
    firedRef.current.add(key);

    (async () => {
      const eng = engineRef.current;
      let idx = null;
      if (songId) {
        const toastId = toast.loading("Opening your song in the studio…");
        try {
          const song = await getSong(songId);
          const existing = eng.channels.findIndex((c) => c?.buffer && c.sourceUrl && c.sourceUrl === song.audio_url);
          if (existing >= 0) {
            idx = existing;
          } else {
            [idx] = eng.freeChannelIndices(1);
            if (idx == null) throw new Error("The mixer is full — clear a channel first.");
            await loadUrlIntoChannel(eng, idx, playableUrl(song.audio_url), `${song.title || "song"}.mp3`, song.audio_url);
          }
          let vocalIdx = null;
          if (song.vocal?.file_url) {
            try { vocalIdx = await ensureVocalTakeLoaded(eng, song); } catch (e) { void e; }
          }
          setLoadedCount(eng.channels.filter((c) => c?.buffer).length);
          setMaxDur(eng.maxDuration || 0);
          setSelectedChannel(idx);
          if (vocalIdx != null) setVisibleChannels((v) => (vocalIdx >= v ? Math.min(64, vocalIdx + 8) : v));
          setVisibleChannels((v) => (idx >= v ? Math.min(64, idx + 8) : v));
          window.dispatchEvent(new CustomEvent("elpm:jump-to-channel", { detail: { ch: idx } }));
          toast.dismiss(toastId);
          toast.success(`"${song.title || "Song"}" is on CH ${padCh(idx)}${vocalIdx != null ? ` · your voice on CH ${padCh(vocalIdx)}` : ""}`, {
            description: intent ? undefined : "Everything from the Creator is here — full mixer, effects and mastering.",
          });
        } catch (e) {
          toast.dismiss(toastId);
          toast.error("Couldn't open the song here", { description: e?.message || "Try again from My Projects." });
        }
      }
      const chNum = chParam != null && chParam !== "" ? Number(chParam) : NaN;
      if (Number.isInteger(chNum) && chNum >= 0 && chNum < 64) {
        setSelectedChannel(chNum);
        setVisibleChannels((v) => (chNum >= v ? Math.min(64, chNum + 8) : v));
        window.dispatchEvent(new CustomEvent("elpm:jump-to-channel", { detail: { ch: chNum } }));
      }
      if (intent) {
        const act = actionsRef.current[intent];
        try { act?.(Number.isInteger(chNum) ? chNum : idx); } catch (e) { void e; }
        const hint = INTENT_HINTS[intent];
        if (hint) toast.message(hint.title, { description: hint.description, duration: 7000 });
      }
      navigate({ pathname: location.pathname, search: "" }, { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
}
